// ============================================================
// DocsPanel
// ============================================================
// MOBILE changes:
//   - Outer panel div gets className="docs-panel" so mobile.css
//     can override width to 100vw on phone.
//   - Nav button padding bumped to py-3 for larger touch targets.
//   - All other code is identical to the previous version.
// ============================================================

import React, { useState } from 'react';

interface DocsPanelProps {
  open: boolean;
  onClose: () => void;
}

interface NavSection { id: string; label: string; icon: string; }

const NAV_SECTIONS: NavSection[] = [
  { id: 'overview',    label: 'Overview',        icon: '◎' },
  { id: 'chord-entry', label: 'Chord entry',     icon: '♩' },
  { id: 'display',     label: 'Display modes',   icon: '◈' },
  { id: 'rhythm',      label: 'Rhythm input',    icon: '♪' },
  { id: 'transpose',   label: 'Key & transpose', icon: '⇅' },
  { id: 'playback',    label: 'Playback',        icon: '▶' },
  { id: 'annotations', label: 'Annotations',     icon: '✏' },
  { id: 'sections',    label: 'Sections & bars', icon: '▤' },
  { id: 'library',     label: 'Library',         icon: '☰' },
  { id: 'cloud',       label: 'Cloud & sharing', icon: '⬡' },
  { id: 'shortcuts',   label: 'Shortcuts',       icon: '⌨' },
];

function DocH2({ children }: { children: React.ReactNode }) {
  return <h3 style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-ink-primary)', margin: '14px 0 6px' }}>{children}</h3>;
}
function DocP({ children }: { children: React.ReactNode }) {
  return <p style={{ fontSize: 12, color: 'var(--color-ink-secondary)', lineHeight: 1.6, marginBottom: 8 }}>{children}</p>;
}
function DocTip({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 11, background: 'var(--color-hover)',
      borderLeft: '2px solid var(--color-rule)', padding: '6px 10px',
      borderRadius: '0 4px 4px 0', color: 'var(--color-ink-secondary)',
      margin: '8px 0', lineHeight: 1.5,
    }}>{children}</div>
  );
}
function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd style={{
      display: 'inline-block', fontFamily: 'var(--font-mono)', fontSize: 10,
      background: 'var(--color-hover)', border: '0.5px solid var(--color-rule)',
      borderRadius: 3, padding: '1px 5px', color: 'var(--color-ink-primary)',
    }}>{children}</kbd>
  );
}
function FeatureRow({ icon, name, desc }: { icon: string; name: string; desc: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
      <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-ink-primary)', marginBottom: 2 }}>{name}</div>
        <div style={{ fontSize: 11, color: 'var(--color-ink-secondary)', lineHeight: 1.5 }}>{desc}</div>
      </div>
    </div>
  );
}
function ShortcutRow({ action, keys }: { action: string; keys: string[] }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '0.5px solid var(--color-rule)' }}>
      <span style={{ fontSize: 11, color: 'var(--color-ink-secondary)' }}>{action}</span>
      <div style={{ display: 'flex', gap: 3 }}>{keys.map((k, i) => <Kbd key={i}>{k}</Kbd>)}</div>
    </div>
  );
}

interface BoxProps { x: number; y: number; w: number; h: number; active?: boolean; rx?: number; }
function Box({ x, y, w, h, active, rx = 4 }: BoxProps) {
  return (
    <rect x={x} y={y} width={w} height={h} rx={rx} style={{
      fill: active ? 'var(--color-hover)' : 'var(--color-paper-alt, var(--color-hover))',
      stroke: active ? 'var(--color-ink-muted)' : 'var(--color-rule)',
      strokeWidth: active ? 1 : 0.5,
    }} />
  );
}
function Label({ x, y, children, size = 9, muted = false, mono = false }: { x: number; y: number; children: React.ReactNode; size?: number; muted?: boolean; mono?: boolean; }) {
  return (
    <text x={x} y={y} textAnchor="middle" style={{ fontSize: size, fill: muted ? 'var(--color-ink-muted)' : 'var(--color-ink-secondary)', fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans, sans-serif)' }}>{children}</text>
  );
}
function ChordText({ x, y, children }: { x: number; y: number; children: React.ReactNode }) {
  return <text x={x} y={y} textAnchor="middle" style={{ fontSize: 16, fontWeight: 600, fill: 'var(--color-ink-primary)', fontFamily: 'var(--font-display, Georgia, serif)' }}>{children}</text>;
}
function SectionTitle({ x, y, children }: { x: number; y: number; children: React.ReactNode }) {
  return <text x={x} y={y} style={{ fontSize: 8, fontWeight: 500, fill: 'var(--color-ink-muted)', fontFamily: 'var(--font-sans, sans-serif)', letterSpacing: '0.08em' }}>{children}</text>;
}
function HRule({ x1, y, x2 }: { x1: number; y: number; x2: number }) {
  return <line x1={x1} y1={y} x2={x2} y2={y} style={{ stroke: 'var(--color-rule)', strokeWidth: 0.5 }} />;
}
function VRule({ x, y1, y2 }: { x: number; y1: number; y2: number }) {
  return <line x1={x} y1={y1} x2={x} y2={y2} style={{ stroke: 'var(--color-rule)', strokeWidth: 0.5 }} />;
}
function Arrow({ x1, y1, x2, y2 }: { x1: number; y1: number; x2: number; y2: number }) {
  return <line x1={x1} y1={y1} x2={x2} y2={y2} markerEnd="url(#docs-arrow)" style={{ stroke: 'var(--color-ink-muted)', strokeWidth: 0.5 }} />;
}
function ArrowDefs() {
  return (
    <defs>
      <marker id="docs-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
        <path d="M2 1L8 5L2 9" fill="none" style={{ stroke: 'var(--color-ink-muted)' }} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </marker>
    </defs>
  );
}

// ── Section components ────────────────────────────────────────
// (All section components identical to previous version — omitted for brevity,
//  kept in full below)

function SectionOverview() {
  const groups = [
    { w: 36, label: '↩ ↪',   sub: 'History' },
    { w: 50, label: 'C  − +', sub: 'Key' },
    { w: 36, label: 'NNS',   sub: 'Display' },
    { w: 46, label: '♩ 120', sub: 'Tempo' },
    { w: 46, label: 'ANA ✏', sub: 'Analysis' },
    { w: 34, label: '☰  ?',  sub: 'Files' },
  ];
  let cursor = 6;
  return (
    <>
      <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-ink-primary)', marginBottom: 4 }}>Welcome to ChartFlow</h2>
      <DocP>A chord sheet editor for working musicians. Enter chords, transpose to any key, add rhythm, and listen back — all in a lead-sheet aesthetic built for print and performance.</DocP>
      <svg width="100%" viewBox="0 0 300 52" style={{ margin: '10px 0', display: 'block' }}>
        <Box x={0} y={0} w={300} h={52} rx={6} />
        {groups.map(({ w, label, sub }, i) => {
          const x = cursor; cursor += w + 4;
          return (
            <g key={i}>
              <Box x={x} y={7} w={w} h={38} />
              <Label x={x + w / 2} y={22}>{label}</Label>
              <Label x={x + w / 2} y={35} muted>{sub}</Label>
            </g>
          );
        })}
      </svg>
      <DocH2>Quick start</DocH2>
      <FeatureRow icon="1️⃣" name="Click any bar" desc="Type a chord in Nashville (1, 4, 5m7), Roman numerals (I, IVmaj7), or chord names (C, Am7). Press Tab to move to the next bar." />
      <FeatureRow icon="2️⃣" name="Set your key" desc="Click the key badge in the toolbar. Use − / + to transpose up or down by semitone." />
      <FeatureRow icon="3️⃣" name="Press play" desc="Click ▶. The active chord highlights in real time as the chart plays back through a piano synth." />
    </>
  );
}

function SectionChordEntry() {
  const cols = [
    { title: 'Nashville', lines: ['1', '4', '5m7'], sub: 'Key-agnostic' },
    { title: 'Roman',     lines: ['I', 'IV', 'vm7'], sub: 'Case = quality' },
    { title: 'Chord names', lines: ['C', 'F', 'Gm7'], sub: 'Needs key set' },
  ];
  return (
    <>
      <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-ink-primary)', marginBottom: 4 }}>Chord entry</h2>
      <DocP>Click any bar to enter edit mode, then type your chord. Three input formats are accepted simultaneously.</DocP>
      <svg width="100%" viewBox="0 0 300 120" style={{ margin: '10px 0', display: 'block' }}>
        {cols.map(({ title, lines, sub }, ci) => (
          <g key={ci}>
            <Box x={ci * 102} y={0} w={96} h={112} rx={6} />
            <Label x={ci * 102 + 48} y={15} size={9}>{title}</Label>
            {lines.map((l, li) => <ChordText key={li} x={ci * 102 + 48} y={38 + li * 24}>{l}</ChordText>)}
            <Label x={ci * 102 + 48} y={102} muted size={8}>{sub}</Label>
          </g>
        ))}
      </svg>
      <DocTip>Slash chords: type <Kbd>4/1</Kbd> or <Kbd>F/C</Kbd> — the bass note goes after the slash and is played by the synth in the bass register.</DocTip>
      <DocH2>Supported qualities</DocH2>
      <DocP>maj, min (m), dom7 (7), maj7, min7 (m7), dim, dim7, hdim7 (ø), aug, sus2, sus4, min6, maj6, add9, 9, maj9, min9, 11, 13, power (5).</DocP>
      <DocH2>Navigation</DocH2>
      <ShortcutRow action="Next bar"      keys={['Tab']} />
      <ShortcutRow action="Previous bar"  keys={['⇧', 'Tab']} />
      <ShortcutRow action="Move bars"     keys={['←', '→']} />
      <ShortcutRow action="Confirm entry" keys={['Enter']} />
    </>
  );
}

function SectionDisplay() {
  const modes = ['NNS', 'Roman', 'Chord names'];
  const examples = ['1  4  5', 'I  IV  V', 'C  F  G'];
  return (
    <>
      <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-ink-primary)', marginBottom: 4 }}>Display modes</h2>
      <DocP>The NNS button cycles between three views. The underlying chord data never changes — only the rendering.</DocP>
      <svg width="100%" viewBox="0 0 300 62" style={{ margin: '10px 0', display: 'block' }}>
        {modes.map((label, i) => (
          <g key={label}>
            <Box x={i * 100 + 4} y={6} w={90} h={28} rx={5} active={i === 0} />
            <text x={i * 100 + 49} y={24} textAnchor="middle" style={{ fontSize: 11, fontWeight: i === 0 ? 500 : 400, fill: i === 0 ? 'var(--color-ink-primary)' : 'var(--color-ink-secondary)', fontFamily: 'var(--font-sans, sans-serif)' }}>{label}</text>
            <Label x={i * 100 + 49} y={52} muted size={9}>{examples[i]}</Label>
          </g>
        ))}
      </svg>
      <FeatureRow icon="𝄞" name="Nashville (NNS)" desc="Degrees 1–7. Key-agnostic — the chart reads the same in every key. Best for session work and modulating tunes." />
      <FeatureRow icon="♭" name="Roman numerals" desc="Uppercase = major, lowercase = minor. Quality follows case — iim7 rather than IIm7." />
      <FeatureRow icon="𝄢" name="Chord names" desc="Absolute note names based on the current key (C, Am7, G7). Requires a key to be set." />
      <DocTip>Harmonic analysis labels (ANA) appear between the chord symbol and slash marks, showing Roman numeral function in context.</DocTip>
    </>
  );
}

function SectionRhythm() {
  const notes = ['𝅝', '𝅗𝅥', '♩', '♪', '♬', '3'];
  const subs  = ['whole', 'half', 'quarter', 'eighth', '16th', 'triplet'];
  return (
    <>
      <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-ink-primary)', marginBottom: 4 }}>Rhythm input</h2>
      <DocP>Enable rhythm mode on any bar to lay down a custom rhythmic pattern, then assign chords to each slot.</DocP>
      <svg width="100%" viewBox="0 0 300 84" style={{ margin: '10px 0', display: 'block' }}>
        <Label x={150} y={13}>Note value palette</Label>
        {notes.map((label, i) => (
          <g key={i}>
            <Box x={i * 46 + 8} y={18} w={40} h={38} />
            <text x={i * 46 + 28} y={41} textAnchor="middle" style={{ fontSize: 14, fill: 'var(--color-ink-primary)', fontFamily: 'serif' }}>{label}</text>
            <Label x={i * 46 + 28} y={52} muted size={7}>{subs[i]}</Label>
          </g>
        ))}
        <Label x={150} y={72}>Beat counter — green when bar is full</Label>
        <Box x={30} y={75} w={240} h={8} rx={3} />
        <rect x={30} y={75} width={160} height={8} rx={3} style={{ fill: 'var(--color-accent)', opacity: 0.35 }} />
      </svg>
      <FeatureRow icon="①" name="Open rhythm mode" desc="Click a bar, then click the rhythm icon. A palette of note values appears above the bar." />
      <FeatureRow icon="②" name="Input note values" desc="Click notes or press shortcut keys. The beat counter shows your total vs the time signature." />
      <FeatureRow icon="③" name="Assign chords" desc="Click each slot and type a chord. Slots without a chord display as rest placeholders." />
      <DocTip>Triplets work in all note values — enable the triplet toggle then click a note value. A quarter triplet = ²⁄₃ of a beat.</DocTip>
    </>
  );
}

function SectionTranspose() {
  return (
    <>
      <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-ink-primary)', marginBottom: 4 }}>Key & transpose</h2>
      <DocP>Chords are stored as scale degrees, so transposition is instant and lossless — just a key swap.</DocP>
      <svg width="100%" viewBox="0 0 300 62" style={{ margin: '10px 0', display: 'block' }}>
        <Box x={10} y={8} w={44} h={44} rx={6} />
        <text x={32} y={31} textAnchor="middle" style={{ fontSize: 14, fontWeight: 500, fill: 'var(--color-ink-primary)', fontFamily: 'var(--font-mono)' }}>C</text>
        <Label x={32} y={45} muted size={8}>Key</Label>
        {[{ x: 62, l: '−' }, { x: 98, l: '+' }].map(({ x, l }) => (
          <g key={x}>
            <Box x={x} y={14} w={30} h={32} rx={5} />
            <Label x={x + 15} y={34} size={14}>{l}</Label>
          </g>
        ))}
        <Label x={90} y={57} muted size={8}>Semitone</Label>
        <VRule x={140} y1={18} y2={46} />
        <Box x={158} y={20} w={130} h={24} rx={4} />
        <text x={223} y={36} textAnchor="middle" style={{ fontSize: 9, fill: 'var(--color-ink-primary)', fontFamily: 'var(--font-mono)' }}>Verse: C → Chorus: Eb</text>
      </svg>
      <FeatureRow icon="⇅" name="Semitone transpose" desc="The − and + buttons shift the entire song by one semitone. Section key overrides are preserved with the correct interval relationship." />
      <FeatureRow icon="♭" name="Section key overrides" desc="Each section can have its own key — useful for modulating songs. Set it in the section header." />
      <FeatureRow icon="🔍" name="Auto key detect" desc="The detect button analyses your chords and suggests the most likely key." />
      <DocTip>Slash chord bass notes are stored as degrees too, so they transpose automatically.</DocTip>
    </>
  );
}

function SectionPlayback() {
  return (
    <>
      <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-ink-primary)', marginBottom: 4 }}>Playback</h2>
      <DocP>ChartFlow plays your chart through a browser piano synth. The active chord highlights in real time.</DocP>
      <svg width="100%" viewBox="0 0 300 66" style={{ margin: '10px 0', display: 'block' }}>
        <circle cx={28} cy={30} r={18} style={{ fill: 'var(--color-hover)', stroke: 'var(--color-rule)', strokeWidth: 0.5 }} />
        <Label x={28} y={35} size={13}>▶</Label>
        <Label x={28} y={56} muted size={8}>Play/Stop</Label>
        <Box x={58} y={16} w={70} h={28} rx={5} />
        <Label x={93} y={33} size={10}>♩ 120 BPM</Label>
        <Label x={93} y={52} muted size={8}>Tempo</Label>
        <Box x={180} y={10} w={112} h={36} rx={6} />
        <Label x={236} y={26}>Active chord cursor</Label>
        <Box x={185} y={30} w={102} h={10} rx={3} />
        <rect x={185} y={30} width={36} height={10} rx={3} style={{ fill: 'var(--color-accent)', opacity: 0.45 }} />
      </svg>
      <FeatureRow icon="🎹" name="Piano synth" desc="Uses Tone.js PolySynth — triangle oscillator with a piano-style envelope. No samples needed, works instantly." />
      <FeatureRow icon="🎯" name="Chord-level cursor" desc="The cursor fires at the exact beat position of each chord within the bar, not just at bar boundaries." />
      <FeatureRow icon="🔁" name="Repeat support" desc="Playback respects ||: and :|| repeat barlines — bracketed sections play the correct number of times." />
      <DocTip>Slash chord bass notes are played in the bass register alongside the chord voicing above.</DocTip>
    </>
  );
}

function SectionAnnotations() {
  return (
    <>
      <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-ink-primary)', marginBottom: 4 }}>Annotations</h2>
      <DocP>Free-floating text labels you can drag anywhere on the chart — for dynamics, cues, rehearsal letters, or style notes.</DocP>
      <svg width="100%" viewBox="0 0 300 82" style={{ margin: '10px 0', display: 'block' }}>
        <Box x={10} y={6} w={280} h={66} rx={6} />
        <Label x={150} y={24} muted>Chart area — annotation mode active (✏ in toolbar)</Label>
        <Box x={28} y={32} w={82} h={24} rx={4} />
        <text x={69} y={47} textAnchor="middle" style={{ fontSize: 10, fontStyle: 'italic', fill: 'var(--color-ink-primary)', fontFamily: 'var(--font-display, Georgia, serif)' }}>mp, legato</text>
        <Box x={168} y={28} w={100} h={24} rx={4} />
        <text x={218} y={43} textAnchor="middle" style={{ fontSize: 10, fontWeight: 500, fill: 'var(--color-accent)', fontFamily: 'var(--font-display, Georgia, serif)' }}>🅐 Rehearsal A</text>
        <circle cx={28} cy={32} r={3} style={{ fill: 'var(--color-ink-muted)', opacity: 0.6 }} />
        <circle cx={168} cy={28} r={3} style={{ fill: 'var(--color-ink-muted)', opacity: 0.6 }} />
      </svg>
      <FeatureRow icon="✏" name="Enable annotation mode" desc="Click the ✏ button in the toolbar. A dashed overlay appears. Mode exits automatically after placing." />
      <FeatureRow icon="⊕" name="Place an annotation" desc="Click anywhere on the chart. A text editor and style popover appear immediately." />
      <FeatureRow icon="⤢" name="Drag to reposition" desc="Annotations are freely draggable. Position is stored as a ratio of chart width, surviving resizing." />
      <DocTip>Style each annotation with custom color, font size, italic, and bold from the popover that appears on click.</DocTip>
    </>
  );
}

function SectionSections() {
  const bars = [
    { chord: '1', slashes: '/ / / /', accent: false },
    { chord: '4', slashes: '/ / / /', accent: false },
    { chord: '5', slashes: '/ / /',   accent: true  },
    { chord: '1', slashes: 'tacet',   accent: false },
  ];
  return (
    <>
      <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-ink-primary)', marginBottom: 4 }}>Sections & bars</h2>
      <DocP>A chart is made of sections (Verse, Chorus, Bridge…), each containing rows of bars. Settings cascade song → section → bar, with overrides at any level.</DocP>
      <svg width="100%" viewBox="0 0 300 92" style={{ margin: '10px 0', display: 'block' }}>
        <Box x={10} y={6} w={280} h={78} rx={8} />
        <SectionTitle x={22} y={22}>VERSE</SectionTitle>
        <Label x={90} y={22} muted mono size={8}>Key: C</Label>
        {bars.map(({ chord, slashes, accent }, i) => (
          <g key={i}>
            <Box x={16 + i * 66} y={28} w={60} h={50} rx={4} active={accent} />
            <ChordText x={16 + i * 66 + 30} y={52}>{chord}</ChordText>
            <Label x={16 + i * 66 + 30} y={68} muted size={8}>{slashes}</Label>
          </g>
        ))}
      </svg>
      <FeatureRow icon="𝄆" name="Repeat barlines" desc="Toggle ||: (repeat start) and :|| (repeat end with count) on any bar. Playback and PDF both respect these." />
      <FeatureRow icon="⅞" name="Time signature override" desc="Any bar can carry its own time signature — useful for a bar of 3 inside a 4/4 chart." />
      <FeatureRow icon="♪♪" name="8th note subdivisions" desc="Toggle to show subdivision marks between the beat slashes in any bar." />
      <FeatureRow icon="📝" name="Bar notes & lyrics" desc="Each bar has optional freetext note (tacet, fermata…) and lyrics fields shown below the chord symbol." />
    </>
  );
}

function SectionLibrary() {
  const folders = ['📁 Setlists', '   My gig chart', '   Jazz standards', '📁 Originals'];
  const songs   = ['Autumn Leaves', 'Blue Bossa', 'So What'];
  return (
    <>
      <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-ink-primary)', marginBottom: 4 }}>Library</h2>
      <DocP>The ☰ Charts button opens the chart library — a full-screen drawer for managing all your charts and folders.</DocP>
      <svg width="100%" viewBox="0 0 300 82" style={{ margin: '10px 0', display: 'block' }}>
        <Box x={10} y={6} w={128} h={68} rx={6} />
        {folders.map((t, i) => (
          <text key={i} x={22} y={26 + i * 14} style={{ fontSize: 9, fill: i === 0 || i === 3 ? 'var(--color-ink-secondary)' : 'var(--color-ink-muted)', fontFamily: 'var(--font-sans, sans-serif)' }}>{t}</text>
        ))}
        <Box x={148} y={6} w={142} h={68} rx={6} />
        <Label x={160} y={22} muted size={8}>⌕ Search charts…</Label>
        <HRule x1={148} y={28} x2={290} />
        {songs.map((t, i) => (
          <text key={i} x={160} y={43 + i * 14} style={{ fontSize: 9, fill: i === 0 ? 'var(--color-ink-primary)' : 'var(--color-ink-secondary)', fontFamily: 'var(--font-sans, sans-serif)' }}>{t}</text>
        ))}
      </svg>
      <FeatureRow icon="📁" name="Folders" desc="Create folders, drag charts between them, rename by double-clicking. Folders sync to the cloud when signed in." />
      <FeatureRow icon="⌕" name="Search" desc="Live search filters all charts across all folders as you type." />
      <FeatureRow icon="···" name="Chart menu" desc="Each chart has an overflow menu for: Open, Rename, Duplicate, Delete." />
      <DocTip>Charts autosave to localStorage every 30 seconds and when you close the tab. Sign in to sync everything to the cloud.</DocTip>
    </>
  );
}

function SectionCloud() {
  return (
    <>
      <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-ink-primary)', marginBottom: 4 }}>Cloud & sharing</h2>
      <DocP>Sign in to sync charts across devices and collaborate with other musicians.</DocP>
      <svg width="100%" viewBox="0 0 300 62" style={{ margin: '10px 0', display: 'block' }}>
        <ArrowDefs />
        <Box x={8} y={14} w={72} h={32} rx={6} />
        <Label x={44} y={34}>Your chart</Label>
        <Arrow x1={80} y1={30} x2={112} y2={30} />
        <rect x={112} y={8} width={76} height={44} rx={6} style={{ fill: 'var(--color-accent)', opacity: 0.12, stroke: 'var(--color-accent)', strokeWidth: 0.5 }} />
        <text x={150} y={28} textAnchor="middle" style={{ fontSize: 9, fill: 'var(--color-accent)', fontFamily: 'var(--font-sans, sans-serif)' }}>Supabase</text>
        <text x={150} y={42} textAnchor="middle" style={{ fontSize: 8, fill: 'var(--color-accent)', fontFamily: 'var(--font-sans, sans-serif)', opacity: 0.7 }}>cloud sync</text>
        <Arrow x1={188} y1={30} x2={218} y2={30} />
        <Box x={218} y={14} w={74} h={32} rx={6} />
        <Label x={255} y={27} size={8}>Collaborator</Label>
        <Label x={255} y={39} muted size={8}>(viewer/editor)</Label>
      </svg>
      <FeatureRow icon="☁" name="Auto cloud sync" desc="Charts push to Supabase on every autosave and on page unload. Open the same chart on any device by signing in." />
      <FeatureRow icon="⤷" name="Share a chart" desc="Click Share and enter a collaborator's email. Assign viewer or editor access. They receive an invite notification." />
      <FeatureRow icon="🔔" name="Pending invites" desc="A badge in the toolbar shows pending invites. Click to accept or decline — accepted charts pull to your library." />
    </>
  );
}

function SectionShortcuts() {
  return (
    <>
      <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-ink-primary)', marginBottom: 4 }}>Keyboard shortcuts</h2>
      <DocH2>Navigation</DocH2>
      <ShortcutRow action="Next bar"          keys={['Tab']} />
      <ShortcutRow action="Previous bar"      keys={['⇧', 'Tab']} />
      <ShortcutRow action="Move between bars" keys={['←', '→']} />
      <ShortcutRow action="Next section"      keys={['Enter']} />
      <DocH2>Editing</DocH2>
      <ShortcutRow action="Undo"          keys={['⌘', 'Z']} />
      <ShortcutRow action="Redo"          keys={['⌘', '⇧', 'Z']} />
      <ShortcutRow action="Save / export" keys={['⌘', 'S']} />
      <DocH2>Toolbar</DocH2>
      <ShortcutRow action="Print / save PDF" keys={['⌘', 'P']} />
      <ShortcutRow action="Transpose down"   keys={['⌘', '←']} />
      <ShortcutRow action="Transpose up"     keys={['⌘', '→']} />
      <ShortcutRow action="Toggle analysis"  keys={['⌘', 'A']} />
      <ShortcutRow action="Play / stop"      keys={['Space']} />
      <DocTip>On Windows/Linux, swap ⌘ for Ctrl.</DocTip>
    </>
  );
}

const SECTION_COMPONENTS: Record<string, React.FC> = {
  'overview':    SectionOverview,
  'chord-entry': SectionChordEntry,
  'display':     SectionDisplay,
  'rhythm':      SectionRhythm,
  'transpose':   SectionTranspose,
  'playback':    SectionPlayback,
  'annotations': SectionAnnotations,
  'sections':    SectionSections,
  'library':     SectionLibrary,
  'cloud':       SectionCloud,
  'shortcuts':   SectionShortcuts,
};

// ── Main component ────────────────────────────────────────────

export function DocsPanel({ open, onClose }: DocsPanelProps) {
  const [activeSection, setActiveSection] = useState('overview');
  const ActiveContent = SECTION_COMPONENTS[activeSection];

  return (
    <>
      {open && (
        <div onClick={onClose} style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.18)',
          zIndex: 49,
        }} />
      )}

      {/* MOBILE: "docs-panel" class — mobile.css overrides width to 100vw on phone */}
      <div
        className="docs-panel"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 400,
          background: 'var(--color-paper)',
          borderLeft: '0.5px solid var(--color-rule)',
          display: 'flex', flexDirection: 'column',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.25s ease',
          zIndex: 50,
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '0.5px solid var(--color-rule)',
          flexShrink: 0,
          background: 'var(--color-paper)',
        }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-ink-primary)' }}>
            ChartFlow documentation
          </span>
          <button onClick={onClose} aria-label="Close documentation" style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-ink-secondary)', fontSize: 16,
            lineHeight: 1, padding: '2px 4px', borderRadius: 4,
          }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Nav — MOBILE: py-3 on buttons (was py-2) for larger touch targets */}
          <nav style={{
            width: 130, minWidth: 130,
            borderRight: '0.5px solid var(--color-rule)',
            overflowY: 'auto', padding: '8px 0',
            background: 'var(--color-paper-alt, var(--color-hover))',
          }}>
            {NAV_SECTIONS.map(({ id, label, icon }) => (
              <button key={id} onClick={() => setActiveSection(id)} style={{
                display: 'flex', alignItems: 'flex-start', gap: 6,
                // MOBILE: increased vertical padding from 7px to 10px for touch targets
                padding: '10px 12px', fontSize: 11, cursor: 'pointer',
                color: activeSection === id ? 'var(--color-ink-primary)' : 'var(--color-ink-secondary)',
                background: activeSection === id ? 'var(--color-paper)' : 'transparent',
                fontWeight: activeSection === id ? 500 : 400,
                borderRight: activeSection === id ? '2px solid var(--color-ink-muted)' : '2px solid transparent',
                border: 'none', width: '100%', textAlign: 'left', lineHeight: 1.3,
              }}>
                <span style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                {label}
              </button>
            ))}
          </nav>

          {/* Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: 'var(--color-paper)' }}>
            <ActiveContent />
          </div>
        </div>
      </div>
    </>
  );
}